import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { MARKETING_FAQ_QA } from '@/content/marketingFaq';
import { cn } from '@/lib/utils';

export type LandingFaqSectionProps = {
  /** На отдельной странице `/faq` заголовок — `<h1>` для SEO и иерархии. */
  standalone?: boolean;
};

function renderFaqAnswer(entry: (typeof MARKETING_FAQ_QA)[number]): ReactNode {
  if (entry.id === 'viewer') {
    return (
      <>
        Вы даёте ссылку на экран таймера (<span className="cf-faq-mono">/viewer/…</span>) или QR из окна шаринга на
        панели. Спикер открывает её на своём устройстве или на экране в зале.
      </>
    );
  }
  if (entry.id === 'free-tier') {
    return (
      <>
        Лимиты: до 3 комнат у владельца, до 3 таймеров и 3 карточек сообщений в комнате, до 3 одновременных
        подключений. Данные не удаляются — сверх лимита доступ закрывается до расширения лицензии.{' '}
        <Link to="/pricing" className="cf-faq-link">
          Цены.
        </Link>
      </>
    );
  }
  return entry.answerPlain;
}

const FAQ_ITEMS = MARKETING_FAQ_QA.map((entry) => ({
  question: entry.question,
  answer: renderFaqAnswer(entry),
}));

/** Секция с якорем #faq; на лендинге — аккордеон, на `/faq` — все ответы сразу. */
export function LandingFaqSection({ standalone = false }: LandingFaqSectionProps) {
  const TitleTag = standalone ? 'h1' : 'h2';
  return (
    <section
      className={cn('cf-section cf-faq-section', standalone && 'cf-faq-section--standalone')}
      id="faq"
      aria-labelledby="cf-faq-heading"
    >
      <div className="cf-faq-inner">
        <TitleTag id="cf-faq-heading" className="cf-faq-title">
          Частые вопросы
        </TitleTag>
        <div className="cf-faq-list">
          {standalone
            ? FAQ_ITEMS.map(({ question, answer }) => (
                <article key={question} className="cf-faq-item">
                  <h3 className="cf-faq-question">{question}</h3>
                  <div className="cf-faq-answer">{answer}</div>
                </article>
              ))
            : FAQ_ITEMS.map(({ question, answer }) => (
                <details key={question} className="cf-faq-details" name="landing-faq">
                  <summary className="cf-faq-summary">
                    <span className="cf-faq-question">{question}</span>
                    <ChevronDown className="cf-faq-chevron" aria-hidden size={22} strokeWidth={2} />
                  </summary>
                  <div className="cf-faq-answer">{answer}</div>
                </details>
              ))}
        </div>
      </div>
    </section>
  );
}
